let pwaServiceWorkerRegistration = null;
let pwaRegistrationError = null;
let pwaRegistrationResolve = null;
const pwaRegistrationPromise = new Promise((resolve) => {
  pwaRegistrationResolve = resolve;
});

/**
 * Store the Service Worker registration returned by `virtual:pwa-register`.
 * This lets Firebase Messaging use the *exact same* SW/scope when calling `getToken`.
 */
export function setPwaServiceWorkerRegistration(registration) {
  pwaServiceWorkerRegistration = registration || null;
  if (registration && pwaRegistrationResolve) {
    pwaRegistrationResolve(registration);
  }
}

export function getPwaServiceWorkerRegistration() {
  return pwaServiceWorkerRegistration;
}

export function setPwaRegistrationError(error) {
  pwaRegistrationError = error;
  if (pwaRegistrationResolve && !pwaServiceWorkerRegistration) {
    pwaRegistrationResolve(null);
  }
}

export function getPwaRegistrationError() {
  return pwaRegistrationError;
}

export function getPwaRegistrationPromise() {
  return pwaRegistrationPromise;
}

function timeout(ms) {
  return new Promise((resolve) => setTimeout(() => resolve(null), ms));
}

/**
 * FCM's `pushManager.subscribe()` throws "no active Service Worker" when the
 * registration has no active worker, so only ever return registrations that
 * actually have one.
 */
function hasActiveWorker(registration) {
  return Boolean(registration?.active);
}

/**
 * Get the active SW registration for the app.
 *
 * Only a registration with an `active` worker is returned — that's what
 * `pushManager.subscribe()` requires.
 *
 * Strategy (tried in order):
 * 1. Stored registration (set by onRegistered callback from pwaClient), if active
 * 2. Poll the stored reg + navigator.serviceWorker in a loop (up to timeoutMs)
 * 3. Fallback to navigator.serviceWorker.getRegistration()
 *
 * Increased timeout (10s) for slow networks where SW download+install+activation
 * can take several seconds.
 */
export async function getReadyServiceWorkerRegistration({
  timeoutMs = 10000,
} = {}) {
  if (hasActiveWorker(pwaServiceWorkerRegistration)) {
    return pwaServiceWorkerRegistration;
  }
  if (typeof window === "undefined") return null;

  const sw = navigator.serviceWorker;
  if (!sw) return null;

  const controller = sw.controller;
  if (controller) {
    try {
      const reg = await sw.getRegistration?.(controller.scriptURL);
      if (hasActiveWorker(reg)) {
        pwaServiceWorkerRegistration = reg;
        return reg;
      }
    } catch (_) {
      // getRegistration can throw for an invalid registration — keep polling.
    }
  }

  const pollInterval = 500;
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    if (hasActiveWorker(pwaServiceWorkerRegistration)) {
      return pwaServiceWorkerRegistration;
    }

    try {
      // `sw.ready` only resolves when the registration has an active worker,
      // which is exactly the state push subscription needs.
      const readyReg = await Promise.race([sw.ready, timeout(pollInterval)]);
      if (hasActiveWorker(readyReg)) {
        pwaServiceWorkerRegistration = readyReg;
        return readyReg;
      }
    } catch (_) {
      // sw.ready rejected (unusual but possible)
    }

    const reg = await sw.getRegistration?.();
    if (hasActiveWorker(reg)) {
      pwaServiceWorkerRegistration = reg;
      return reg;
    }
  }

  const reg = await sw.getRegistration?.();
  if (hasActiveWorker(reg)) {
    pwaServiceWorkerRegistration = reg;
    return reg;
  }

  if (pwaRegistrationError) {
    console.warn(
      "[Doitify] Service Worker registration error:",
      pwaRegistrationError,
    );
  } else {
    console.warn("[Doitify] Service Worker not found after %dms", timeoutMs);
  }

  return null;
}
